import {CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import '../swiper/swiper.js';
import {customElement, property, state} from 'lit/decorators.js';
import {consume} from '@lit/context';
import {
  Article,
  Image,
  Messages,
  OpenSizeLayerContainer,
  Price,
  Product,
  SizeDeterminedContainer,
  SizeSelectionContainer,
  WishlistCollection,
} from '../../../@types/wishlist.js';
import '../wishlist-remove-button/wishlist-remove-button.js';
import '../add-to-cart-button/add-to-cart-button.js';
import '../price/price.js';
import '../notify-me-button/notify-me-button.js';
import '../wishlist-button/wishlist-button.js';
import '../vsp-button/vsp-button.js';
import styles from './wishlist-product.scss';
import grids from '../../../styles/grids.scss';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {productListView, trackWishlistClick} from '../../../scripts/service/cube-tracking-layer.js';
import {filterContext} from '../../../scripts/context/filterContext.js';
import {FilterMenu} from '../../../scripts/filter/filter-menu.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {wishlistService} from '../../../scripts/service/wishlist-service.js';
import {buildPdsUrl} from '../../../scripts/service/build-pds-url.js';
import {productsContext} from '../../../scripts/context/wishlistContext.js';
import {cartContext} from '../../../scripts/context/cartContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {CartState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {productMenuContext} from '../../../scripts/context/menuContext.js';
import {ProductMenuState} from '../../../scripts/context/menu-state.js';
import {mediaQuery} from '../../../scripts/service/media-query.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import saleIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-12_sale.svg';
import cartIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_basket.svg';
import bellIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_bell.svg';
import shirtsIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_shirts.svg';
import chevronDownIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_chevron-down.svg';
import chevronRightIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-12_chevron-right.svg';
import basketCeck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_basket-check.svg';
import boxCeck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_box-check.svg';
import {addPresentationToIcon, addTitleToIcon, hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {classMap} from 'lit/directives/class-map.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';

type TriggerableElement = Element & { trigger: boolean };

function firstOrNull<T>(items: T[]): T | null {
  if (items.length === 0) {
    return null;
  }
  return items[0];
}

@customElement('ents-wishlist-product')
export class WishlistProduct extends LitElement {
  @consume({context: i18nContext})
  protected messages!: Messages;

  @consume({context: filterContext, subscribe: true})
  private filterMenu!: FilterMenu;

  @consume({context: productsContext, subscribe: true})
  private productsState!: ProductsState;

  @consume({context: productMenuContext, subscribe: true})
  private productMenuState!: ProductMenuState;

  @consume({context: sizeLayerContext, subscribe: true})
  private sizeLayerState!: SizeLayerState;

  @consume({context: cartContext, subscribe: true})
  @state()
  private cartState!: CartState;

  @property({type: Object, attribute: 'collection'})
  private collection?: WishlistCollection;

  @property({type: Object, attribute: 'product'})
  private product!: Product;

  @property({type: String, attribute: 'selected-article-id'})
  private selectedArticleId?: string;

  @property({type: Number, attribute: 'position'})
  private position!: number;

  @property({type: Number, attribute: 'list-count'})
  private countListItems!: number;

  @property({type: Boolean, attribute: 'logged-in'})
  private loggedIn: boolean = false;

  @property({type: Object, reflect: false})
  private viewportObserver: IntersectionObserver | null = null;

  @property({type: Object, reflect: false})
  private trackingObserver: IntersectionObserver | null = null;

  @property({type: Object, reflect: false})
  private promotionObserver: MutationObserver | null = null;

  @property({type: Boolean, attribute: 'model-view-enabled'})
  private modelViewEnabled: boolean = false;

  @property({type: String, attribute: 'sharing-context'})
  private sharingContext: 'curated_collection' | 'collection' | 'not_shared' = 'not_shared';

  // === abtest-ents-t6727-favoriten-artikelkachel-redesign Product Tile Redesign START ===
  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @state()
  private triggerAddToCart: boolean = false;

  @state()
  private isInViewport: boolean = false;

  @state()
  private triggerNotifyMe: boolean = false;

  @state()
  private selectedArticle: Article | null = null;

  private forcedFullRendering: boolean = false;

  private viewportDebounceTimer?: number;

  private trackingDebounceTimer?: number;

  private hasTrackedView = false;

  private productIdentifier!: ProductIdentifier;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(grids)];
  }

  connectedCallback() {
    super.connectedCallback();
    this.productIdentifier = ProductIdentifier.createByProduct(this.product);
    if (this.selectedArticleId === undefined && this.product.articleId) {
      this.selectedArticleId = this.product.articleId;
      this.setSelectedArticle();
    }

    this.addEventListener('ents:size-layer:open', this.openSizeLayer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('ents:size-layer:open', this.openSizeLayer);
    this.viewportObserver?.unobserve(this);
    this.trackingObserver?.unobserve(this);
    clearTimeout(this.viewportDebounceTimer);
    clearTimeout(this.trackingDebounceTimer);
  }

  protected firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);

    if (this.countListItems >= 100) {
      this.classList.add('large-list');
    }

    this.viewportObserver?.observe(this);
    this.trackingObserver?.observe(this);

    if (!this.product.price) {
      return;
    }

    // product includes a <camp-aktionsflag> element, will need a CAMP API call to render the flag
    this.dispatchEvent(new Event('aktionsflags:schedule-load'));

    if (!this.product.articles.some(a => a.isInPromotion)) {
      // in case we do not know if the product is in a promotion (no articles have `isInPromotion` set to true), we need
      // an observer to set the `isAnyArticleInPromotion` flag on the product once CAMP has performed the API call and
      // updated the attributes of the <camp-aktionsflag> element
      this.registerPromotionObserver();
    }
  }

  protected willUpdate(changedProperties: PropertyValues): void {
    // Only recalculate if inputs changed
    if (changedProperties.has('product') || changedProperties.has('selectedArticleId')) {
      this.setSelectedArticle();
    }
  }

  private isShared() {
    return this.sharingContext === 'collection' || this.sharingContext === 'curated_collection';
  }

  public animateRemoval() {
    if (!this.isShared()) {
      this.style.transition = 'opacity 0.4s linear';
      if (mediaQuery.prefersReducedMotion()) {
        this.style.transition = 'none';
      }
      this.style.opacity = '0';
      const handleTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'opacity') {
          this.style.display = 'none';
          this.removeEventListener('transitionend', handleTransitionEnd);
        }
      };
      this.addEventListener('transitionend', handleTransitionEnd);
    }
  }

  public animateAddition() {
    if (!this.isShared()) {
      this.removeAttribute('style');
    }
  }

  public resetTriggers() {
    this.triggerNotifyMe = false;
    this.triggerAddToCart = false;
  }

  public handleAddToCart(detail: SizeDeterminedContainer) {
    if (detail.syan !== this.product.syan) {
      return;
    }
    if (this.product.articles.find(a => a.id === detail.articleId) == null) {
      return;
    }

    this.selectedArticleId = detail.articleId;
    this.setSelectedArticle();

    if (this.selectedArticle !== null) {
      this.triggerAddToCart = !this.selectedArticle.isOutOfStock;
      this.triggerNotifyMe = this.selectedArticle.isOutOfStock;
    }
  }

  public async handleSizeSelection(detail: SizeSelectionContainer) {
    await this.applySelectedArticle(detail.articleId, detail.triggerCta);
  }

  private setSelectedArticle() {
    if (!this.product.hasSizeOrQuantitySelection) {
      this.selectedArticle = firstOrNull(this.product.articles);
    } else if (this.selectedArticleId === undefined) {
      this.selectedArticle = null;
    } else {
      this.selectedArticle = this.product.articles.find(a => a.id === this.selectedArticleId) || null;
    }
  }

  private registerPromotionObserver() {
    const flagElement = this.shadowRoot?.querySelector('camp-aktionsflag');
    if (!flagElement || !this.promotionObserver) {
      return;
    }

    this.promotionObserver.observe(flagElement, {
      attributes: true,
      attributeFilter: ['contains-flag', 'artikel-id']
    });
  }

  public handlePromotionDetected(artikelId: string | null) {
    if (artikelId) {
      this.product.articles = this.product.articles.map(a =>
        a.id === artikelId ? {...a, isInPromotion: true} : a
      );
    } else {
      this.product.isAnyArticleInPromotion = true;
    }
  }

  private async applySelectedArticle(articleId: string, triggerCta: boolean) {
    this.selectedArticleId = articleId;
    this.setSelectedArticle();
    if (this.selectedArticle === null) {
      return;
    }

    this.triggerNotifyMe = triggerCta && this.selectedArticle.isOutOfStock;
    this.triggerAddToCart = triggerCta && !this.selectedArticle.isOutOfStock;

    if (!this.isShared()) {
      await wishlistService.changeItemOnWishlist(this.product.syan, this.product.colorId, this.selectedArticle.id);
    }
    this.productsState.updateSelectedArticle(this.productIdentifier, this.selectedArticle.id);
  }

  private openSizeLayer = (e: CustomEvent<OpenSizeLayerContainer>) => {
    this.sizeLayerState.update({
      product: this.product,
      trackingContext: e.detail.tracking.context,
      triggerA2C: e.detail.addToCartAfterSizeSelection,
      open: true,
      selectedArticle: this.selectedArticle,
      backButtonCallback: undefined,
    });
  };

  private openSizeLayerDirect = () => {
    this.sizeLayerState.update({
      product: this.product,
      trackingContext: 'wishlist',
      triggerA2C: false,
      open: true,
      selectedArticle: this.selectedArticle,
      backButtonCallback: undefined,
    });
  };

  private getImages() {
    return this.getAttrFromSelected((a: Article) => a.imageUrls, (p: Product) => p.defaultImageUrls);
  }

  private selectInitialImageIndex(images: Image[]): number {
    if (this.modelViewEnabled) {
      const modelViewIndex = images.findIndex(image => image.isModelView);
      return modelViewIndex !== -1 ? modelViewIndex : 0;
    }
    return 0;
  }

  private getPdsUrl() {
    return buildPdsUrl(this.product.pdsUrlBasePath, this.selectedArticleId, this.product.colorId);
  }

  private getTrackingUrl() {
    // for tracking it is ok to hardcode the PROD host (cleared with PO)
    return 'https://www.breuninger.com' + buildPdsUrl(this.product.pdsUrlBasePath, this.selectedArticleId, this.product.colorId);
  }

  private getPrice(): Price | undefined {
    return this.getAttrFromSelected((a: Article) => a.price, (p: Product) => p.price);
  }

  private getMixAndMatch(): string | undefined {
    return this.getAttrFromSelected((a: Article) => a.mixAndMatchMessage, (p: Product) => p.mixAndMatchMessage);
  }

  private getLimited(): string | undefined {
    return this.getAttrFromSelected((a: Article) => a.limitedMessage, (p: Product) => p.limitedMessage);
  }

  private getExclusive(): string | undefined {
    return this.getAttrFromSelected((a: Article) => a.exclusiveMessage, (p: Product) => p.exclusiveMessage);
  }

  private getCertifiedMessage(): string | undefined {
    return this.getAttrFromSelected((a: Article) => a.certifiedMessage, (p: Product) => p.certifiedMessage);
  }

  private showNotificationButton(): boolean {
    return this.product.price !== undefined && this.getAttrFromSelected((a: Article) => a.isOutOfStock, (p: Product) => p.isOutOfStock);
  }

  private getSizeOrQuantity() {
    return this.getAttrFromSelected((a: Article) => a.sizeOrQuantity, () => '');
  }

  private getAttrFromSelected<T>(
    fromSelected: (article: Article) => T,
    defaultAttr: (product: Product) => T
  ): T {
    return this.selectedArticle !== null ? fromSelected(this.selectedArticle) : defaultAttr(this.product);
  }

  private renderNotificationButton(options?: { inlineHidden?: boolean }) {
    return html`
      <ents-notify-me-button
        identifier="${this.productIdentifier.id}"
        salesinformation-id="${this.selectedArticle ? this.selectedArticle.salesInformationId : ''}"
        ?trigger="${this.triggerNotifyMe}"
        .additionalInfoForAriaLabel="${this.getProductInfo()}"
        data-test-ents-notify-me-button="${ifDefined(options?.inlineHidden ? undefined : this.product.syan)}"
        ?data-inline-hidden-notify="${options?.inlineHidden ?? false}"
      >
      </ents-notify-me-button>
    `;
  }

  private getProductInfo(): string {
    const sizeInfo = this.product.hasSizeOrQuantitySelection
      ? (this.getInputWithLabelIfExists('ents_wishlist_product_size', this.getSizeOrQuantity())
        ?? this.messages['ents_wishlist_product_selectSize'])
      : undefined;
    return this.filterUndefinedAndJoin([
      this.getSmallProductInfo(),
      this.getInputWithLabelIfExists('ents_wishlist_product_color', this.product.colorName),
      this.getOutOfStockMessage(),
      this.getInputWithLabelIfExists(
        'ents_wishlist_product_price_currentPrice_label',
        this.getPrice()?.currentPriceMessage,
      ),
      sizeInfo,
    ]);
  }

  private getSmallProductInfo(): string {
    return this.filterUndefinedAndJoin([
      this.product.brandName,
      this.product.name,
      this.product.subName
    ]);
  }

  private filterUndefinedAndJoin(values: (string | undefined)[]): string {
    return values.filter((v): v is string => v !== undefined).join(' ');
  }

  private getInputWithLabelIfExists(
    labelKey: string,
    value?: string,
  ) {
    return value ? `${this.messages[labelKey]} ${value}` : undefined;
  }

  private renderAddToCartButton(options?: { inlineHidden?: boolean }) {
    return html`
      <ents-add-to-cart-button
        identifier="${this.productIdentifier.id}"
        .additionalInfoForAriaLabel="${this.getProductInfo()}"
        article-id="${this.selectedArticle ? this.selectedArticle.id : ''}"
        sales-information-id="${this.selectedArticle ? this.selectedArticle.salesInformationId : ''}"
        ?trigger="${this.triggerAddToCart}"
        data-test-ents-add-to-cart-button="${ifDefined(options?.inlineHidden ? undefined : this.product.syan)}"
        ?data-inline-hidden-add="${options?.inlineHidden ?? false}"
      >
      </ents-add-to-cart-button>
    `;
  }

  private renderVspButton() {
    if (!this.product.isVspButtonEnabled || this.wishlistAppConfig.productTileRedesign) {
      return nothing;
    }
    return html`
      <ents-vsp-button
        .name="${this.getSmallProductInfo()}"
        syan="${this.product.syan}"
        color-id="${this.product.colorId}"
        data-test-ents-vsp-button="${this.product.syan}"
      ></ents-vsp-button>
    `;
  }

  private renderSwiper(available: boolean) {
    const images = this.getImages();
    const initialImageIndex = this.selectInitialImageIndex(images);

    return html`
      <ents-swiper
        class="${ifDefined(this.wishlistAppConfig.productTileRedesign ? 'ents-swiper--redesign' : undefined)}"
        position="${this.position}"
        initial-image-index="${initialImageIndex}"
        .images="${images}"
        ?is-faded="${!available}"
        ?in-viewport="${this.isInViewport}"
        .product="${this.product}"
        alt-text="${this.product.brandName} ${this.product.name}">
      </ents-swiper>`;
  }

  private renderFlags() {
    return html`
      <div
        class="ents-wishlist-flags"
        data-test-ents-wishlist-flags
      >
        <camp-aktionsflag
          class="ents-wishlist-flags--item ents-wishlist-flags--item__campaign-flag"
          data-test-ents-wishlist-camp-ssi
          syan="${this.product.syan}"
          farb-id="${this.product.colorId}"
          artikel-id="${this.selectedArticleId ? this.selectedArticleId : this.product.articleId}">
        </camp-aktionsflag>
        ${this.getPrice()?.saleFlagMessage ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item ents-wishlist-flags--item__sale" variant="sale"
               size="small">
            ${addTitleToIcon(saleIcon(), this.messages['ents_wishlist_filter_option_onSale'])}
          </div>
        ` : nothing}
        ${this.getExclusive() ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${this.getExclusive()}
          </div>
        ` : nothing}
        ${this.getLimited() ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${this.getLimited()}
          </div>
        ` : nothing}
        ${this.product.newMessage && !this.getPrice()?.isReduced ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${this.product.newMessage}
          </div>
        ` : nothing}
        ${this.getCertifiedMessage() ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${this.getCertifiedMessage()}
          </div>
        ` : nothing}
        ${this.getMixAndMatch() ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${unsafeHTML(this.getMixAndMatch())}
          </div>
        ` : nothing}
        ${this.product.easyironMessage ? html`
          <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
            ${this.product.easyironMessage}
          </div>
        ` : nothing}
      </div>
    `;
  }

  private renderNames() {
    return html`
      <div class="ents-wishlist-brand-name">
        ${this.product.brandName}
      </div>
      <div class="ents-wishlist-product-names">
        <div class="ents-wishlist-product-name">
          ${this.product.name}
        </div>
      </div>
    `;
  }

  private renderUnavailableProduct() {
    const gridClasses = this.wishlistAppConfig.productTileRedesign
      ? 'ents-wishlist-product-grid ents-wishlist-product-grid--redesign'
      : 'ents-wishlist-product-grid';
    return html`
      <div class="${gridClasses}" @click="${this.productListClickEvent}">
        <div class="ents-wishlist-swiper-container">
          ${this.renderSwiper(false)}
        </div>
        <div class="ents-wishlist-product-infos">
          ${this.useMinimalRender ? nothing : this.renderNames()}
          ${this.useMinimalRender ? nothing : this.renderColor()}
          ${this.wishlistAppConfig.productTileRedesign || this.useMinimalRender ? nothing : this.renderUnavailableMessage()}
        </div>
      </div>
      ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
      <div class="ents-wishlist-product-cta--container">
        ${this.useMinimalRender ? nothing : this.renderActionButton()}
        ${this.wishlistAppConfig.productTileRedesign && !this.useMinimalRender ? this.renderPrimaryButton() : nothing}
        ${this.useMinimalRender ? nothing : this.renderVspButton()}
      </div>
    `;
  }

  private renderUnavailableMessage() {
    return html`
      <div class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-unavailable-info"
           data-test-ents-wishlist-unavailable-info>${this.product.unavailableMessage}
      </div>`;
  }

  private renderAvailableProduct() {
    const gridClasses = this.wishlistAppConfig.productTileRedesign
      ? 'ents-wishlist-product-grid ents-wishlist-product-grid--redesign'
      : 'ents-wishlist-product-grid';
    return html`
      <div
        class="${gridClasses}">
        <a
          target="_self"
          href="${this.getPdsUrl()}"
          @click="${this.productListClickEvent}"
          aria-label="${this.getSmallProductInfo()}"
          data-test-ents-wishlist-pds-link
        >
          <div
            class="ents-wishlist-pds-container"
            data-test-ents-wishlist-pds-container
          >
            ${this.renderSwiper(true)}
            ${this.renderInCartHint()}
            ${this.renderPurchasedHint()}
          </div>
        </a>
        <a
          target="_self"
          href="${this.getPdsUrl()}"
          @click="${this.productListClickEvent}"
          aria-label="${this.getSmallProductInfo()}"
          data-test-ents-wishlist-pds-link
        >
          <div
            class="ents-wishlist-product-infos"
            data-test-ents-wishlist-product-info
          >
            ${this.renderFlags()}
            ${this.useMinimalRender ? nothing : this.renderNames()}
            ${this.useMinimalRender ? nothing : this.renderColor()}
            ${this.useMinimalRender ? nothing : this.renderSize()}
            ${this.wishlistAppConfig.productTileRedesign || this.useMinimalRender ? nothing : this.renderShortageInfo()}
            ${this.useMinimalRender ? nothing : html`
              <ents-price .price="${this.getPrice()}"></ents-price>`}
          </div>
        </a>
      </div>
      ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
      <div class="ents-wishlist-product-cta--container">
        ${this.useMinimalRender ? nothing : this.renderActionButton()}
        ${this.useMinimalRender ? nothing : this.renderPrimaryButton()}
      </div>
    `;
  }

  private renderColor() {
    return this.product.colorName && this.product.isBeauty ? html`
      <div class="ents-copy-extra-small ents-s:copy-small ents-wishlist-color">
        ${this.messages['ents_wishlist_product_color']}
        ${this.product.colorName}
      </div>
    ` : nothing;
  }

  private renderSize() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return nothing;
    }
    const selectedSizeOrQuantity = this.getSizeOrQuantity();
    return selectedSizeOrQuantity ? html`
      <div class="ents-copy-extra-small ents-s:copy-small ents-wishlist-size" aria-live="polite">
        <span>${this.messages['ents_wishlist_product_size']}</span>
        <span data-test-ents-selected-size>${selectedSizeOrQuantity}</span>
      </div>
    ` : nothing;
  }

  private getOutOfStockMessage() {
    if (!this.product.isOutOfStock && !this.selectedArticle?.isOutOfStock) {
      return undefined;
    }

    if (this.wishlistAppConfig.productTileRedesign) {
      return this.messages['ents_wishlist_product_soldOut_redesign'];
    }

    const isProductWithSelectedSize = this.product.isNotOneSize && this.selectedArticle?.sizeOrQuantity;
    return isProductWithSelectedSize
      ? this.messages['ents_wishlist_product_sizeSoldOut']
      : this.messages['ents_wishlist_product_soldOut'];
  }

  private renderShortageInfo() {
    if (this.selectedArticle?.lowStockMessage) {
      return html`
        <div class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-shortage-info"
             data-test-ents-wishlist-shortage-info>${this.selectedArticle.longLowStockMessage}
        </div>`;
    }

    const outOfStockMessage = this.getOutOfStockMessage();
    if (outOfStockMessage) {
      return html`
        <div class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-out-of-stock-info"
             data-test-ents-wishlist-out-of-stock-info>${outOfStockMessage}
        </div>`;
    }
    return nothing;
  }

  private renderShortageInfoRedesign() {
    if (this.selectedArticle?.lowStockMessage) {
      return html`
        <span class="ents-copy-extra-small"> - </span>
        <span
          class="ents-copy-extra-small ents-wishlist-shortage-message"
          data-test-ents-wishlist-shortage-info>
          ${this.selectedArticle.lowStockMessage}
        </span>`;
    }

    const outOfStockMessage = this.getOutOfStockMessage();
    if (outOfStockMessage) {
      return html`
        ${this.selectedArticle
          ? html`
            <span class="ents-copy-extra-small"> - </span>
            <span
              class="ents-copy-extra-small"
              data-test-ents-wishlist-out-of-stock-info>
              ${outOfStockMessage}
            </span>`
          : html`
            <span
              class="ents-copy-small-bold"
              data-test-ents-wishlist-out-of-stock-info>
              ${outOfStockMessage}
            </span>
          `}
      `;
    }
    return nothing;
  }

  private renderRemoveOrHeartButton() {
    if (this.wishlistAppConfig.productTileRedesign && !this.isShared()) {
      return nothing;
    }

    if (this.isShared()) {
      return this.renderHeartButton();
    } else {
      return this.renderRemoveButton();
    }
  }

  private renderHeartButton() {
    const onOwnWishlist = this.productsState.find(this.productIdentifier) !== undefined;
    const placement = this.sharingContext !== 'not_shared' ? this.sharingContext : '';
    return html`
      <div class="ents-wishlist-product__wishlist-button">
        <ents-wishlist-button
          initial-state="${onOwnWishlist ? [`["${this.productIdentifier.id}"]`] : ['']}"
          syan="${this.product.syan}"
          color-id="${this.product.colorId}"
          size="large"
          position="${this.position}"
          placement="${placement}"
        >
        </ents-wishlist-button>
      </div>
    `;
  }

  private renderRemoveButton() {
    return html`
      <div class="ents-wishlist-product__remove-button">
        <ents-wishlist-remove-button
          .collection="${this.collection}"
          .product="${this.product}"
          data-test-ents-wishlist-remove-button="${this.product.syan}">
          ${this.messages['ents_wishlist_product_cta_remove']}
        </ents-wishlist-remove-button>
      </div>
    `;
  }

  private renderInCartHint() {
    return html`
      ${this.cartState.isInCart(this.product) ? html`
        <div class="bdk-badge-article ents-wishlist-product__in-cart-hint"
             size="medium"
             data-test-ents-wishlist-in-cart-indicator>
          <div>
            ${addTitleToIcon(basketCeck(), this.messages['ents_wishlist_product_in_cart'])}
            <span data-test-ents-wishlist-in-cart-indicator-content>
              ${this.messages['ents_wishlist_product_in_cart']}
            </span>
          </div>
        </div>
      ` : nothing}
    `;
  }

  private renderPurchasedHint() {
    if (this.cartState.isInCart(this.product) || !this.product.purchaseDetails?.orderUrl) return nothing;

    return html`
      <button class="bdk-badge-article ents-wishlist-product__purchased-hint"
              role="button"
              aria-label="${this.messages['ents_wishlist_last_order']}"
              @click="${this.openOrderDrawer}"
              size="medium"
              data-test-ents-wishlist-purchased-indicator>
        <div>
          ${addTitleToIcon(boxCeck(), this.messages['ents_wishlist_last_order'])}
          <span data-test-ents-wishlist-purchased-indicator-content>
            ${this.messages['ents_wishlist_last_order']}
          </span>
        </div>
        ${addPresentationToIcon(chevronRightIcon())}
      </button>
    `;
  }

  private openOrderDrawer(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.updateProductBanner();
    const images = this.getImages();
    const initialImageIndex = this.selectInitialImageIndex(images);
    const imageUrl = images[initialImageIndex].url;
    this.productMenuState.openActionMenu(this.product, this.collection ?? null, imageUrl, this.selectedArticle, 'purchased');
  }

  private renderActionButton() {
    if (this.wishlistAppConfig.productTileRedesign && this.isShared()) {
      return nothing;
    }
    const actionMenuVariant = this.wishlistAppConfig.productTileRedesign ? 'just-icon' : 'outline';
    const actionMenuSize = this.wishlistAppConfig.productTileRedesign ? 'extra-extra-small' : 'medium';
    return html`
      <div class="ents-wishlist-action-button" data-test-ents-action-button-shadow>
        <button class="bdk-button-icon" size="${actionMenuSize}" variant="${actionMenuVariant}"
                type="button" @click="${this.handleActionButtonClick}"
                data-test-ents-action-button
                aria-label="${this.messages['ents_wishlist_actionmenu_button_description']}">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" role="presentation">
            <circle cx="4.667" cy="12" r="2" fill="currentColor"/>
            <circle cx="12.667" cy="12" r="2" fill="currentColor"/>
            <circle cx="20.667" cy="12" r="2" fill="currentColor"/>
          </svg>
        </button>
      </div>`;
  }

  private renderRedesignInlineControls() {
    const showNotification = this.showNotificationButton();
    const showVsp = this.shouldShowVsp();
    const showA2c = this.shouldShowA2c(showVsp, showNotification);
    const sizeSelectionDisabled = this.isSizeSelectionDisabled();

    return html`
      <div class=${classMap({
        'ents-wishlist-product-inline': true,
        'ents-wishlist-product-inline--with-cta': showVsp || showNotification || showA2c
      })}>
        ${this.renderSizeSelectionButton(sizeSelectionDisabled)}
        ${this.renderInlineActionButton(showVsp, showNotification, showA2c)}
      </div>
      <div class="ents-wishlist-product-inline-hidden">
        ${this.renderAddToCartButton({inlineHidden: true})}
        ${this.renderNotificationButton({inlineHidden: true})}
        ${this.product.isVspButtonEnabled ? this.renderHiddenVspButton() : nothing}
      </div>
    `;
  }

  private shouldShowVsp(): boolean {
    return !this.product.price && this.product.isVspButtonEnabled;
  }

  private shouldShowA2c(showVsp: boolean, showNotification: boolean): boolean {
    return !showVsp && !showNotification && this.product.price !== undefined;
  }

  private isSizeSelectionDisabled(): boolean {
    return !this.product.price || this.product.articles.length === 0;
  }

  private getPlaceholderLabel(sizeSelectionDisabled: boolean): string {
    if (sizeSelectionDisabled) {
      return this.product.unavailableMessageRedesign ?? 'Nicht mehr im Sortiment';
    }

    return this.product.hasSizeOrQuantitySelection
      ? this.messages['ents_wishlist_product_selectSize'] ?? 'Bitte Größe wählen'
      : 'one size';
  }

  private getSizeSelectionLabel(sizeSelectionDisabled: boolean): TemplateResult | string {
    if (this.selectedArticle) {
      return this.selectedArticle.sizeOrQuantity ?? 'one size';
    }

    const placeholderLabel = this.getPlaceholderLabel(sizeSelectionDisabled);

    if (sizeSelectionDisabled) {
      return html`<span data-test-ents-wishlist-unavailable-info>${placeholderLabel}</span>`;
    }

    return html`<span>${placeholderLabel}</span>`;
  }

  private getSizeSelectionAriaLabel(sizeSelectionDisabled: boolean): string {
    if (this.selectedArticle) {
      const stockMessage = this.selectedArticle.lowStockMessage ?? this.getOutOfStockMessage() ?? '';
      return `${this.selectedArticle.sizeOrQuantity ?? 'one size'}${stockMessage}`;
    }

    return this.getPlaceholderLabel(sizeSelectionDisabled);
  }

  private renderSizeSelectionButton(sizeSelectionDisabled: boolean) {
    const sizeSelectionLabel = this.getSizeSelectionLabel(sizeSelectionDisabled);
    const sizeSelectionAriaLabel = this.getSizeSelectionAriaLabel(sizeSelectionDisabled);
    const showNotification = this.showNotificationButton();

    return html`
      <button
        class="ents-wishlist-product-size-select"
        aria-live="polite"
        aria-label="${sizeSelectionAriaLabel}"
        @click="${this.openSizeLayerDirect}"
        ?disabled="${sizeSelectionDisabled}"
        data-test-ents-size-select>
        <span class="ents-copy-small ents-wishlist-product-size-select-content"
              data-test-ents-selected-size="${ifDefined(this.selectedArticle ? true : undefined)}">
          ${this.renderSizeSelectionContent(sizeSelectionDisabled, sizeSelectionLabel, showNotification)}
        </span>
        ${!sizeSelectionDisabled ? hideIconFromAssistiveTechnology(chevronDownIcon()) : nothing}
      </button>
    `;
  }

  private renderSizeSelectionContent(
    sizeSelectionDisabled: boolean,
    sizeSelectionLabel: TemplateResult | string,
    showNotification: boolean
  ) {
    const showLabel = this.selectedArticle !== null || sizeSelectionDisabled;
    const showShortage = !sizeSelectionDisabled;
    const showPlaceholder = this.selectedArticle === null && !sizeSelectionDisabled && !showNotification;

    return html`
      ${showLabel ? html`<span class="ents-copy-small-bold">${sizeSelectionLabel}</span>` : nothing}
      ${showShortage ? this.renderShortageInfoRedesign() : nothing}
      ${showPlaceholder ? sizeSelectionLabel : nothing}
    `;
  }

  private renderInlineActionButton(
    showVsp: boolean,
    showNotification: boolean,
    showA2c: boolean
  ) {
    if (!showVsp && !showNotification && !showA2c) {
      return nothing;
    }

    const variant = (showVsp || showNotification) ? 'primary' : 'highlight';
    const ariaLabel = this.getInlineButtonAriaLabel(showVsp, showNotification);
    const icon = this.getInlineButtonIcon(showVsp, showNotification);

    return html`
      <button
        class="bdk-button-icon ents-wishlist-product-inline-button"
        variant="${variant}"
        size="small"
        type="button"
        @click="${this.handleInlinePrimaryClick}"
        data-test-ents-inline-primary-button
        data-test-ents-add-to-cart-button="${ifDefined(showA2c ? this.product.syan : undefined)}"
        data-test-ents-notify-me-button="${ifDefined(showNotification ? this.product.syan : undefined)}"
        data-test-ents-vsp-button="${ifDefined(showVsp ? this.product.syan : undefined)}"
        aria-label="${ariaLabel}"
      >
        ${icon}
      </button>
    `;
  }

  private getInlineButtonAriaLabel(
    showVsp: boolean,
    showNotification: boolean
  ): string {
    if (showVsp) return this.messages['ents_wishlist_product_cta_vsp'] ?? '';
    if (showNotification) return this.messages['ents_wishlist_product_cta_notify'] ?? '';
    return this.messages['ents_wishlist_product_cta_addToCart'] ?? '';
  }

  private getInlineButtonIcon(
    showVsp: boolean,
    showNotification: boolean
  ) {
    if (showVsp) return hideIconFromAssistiveTechnology(shirtsIcon());
    if (showNotification) return hideIconFromAssistiveTechnology(bellIcon());
    return hideIconFromAssistiveTechnology(cartIcon());
  }

  private handleActionButtonClick() {
    this.updateProductBanner();
    const images = this.getImages();
    const initialImageIndex = this.selectInitialImageIndex(images);
    const imageUrl = images[initialImageIndex].url;
    this.productMenuState.openActionMenu(this.product, this.collection ?? null, imageUrl, this.selectedArticle);
  }

  private handleInlinePrimaryClick = () => {
    if (!this.product.price && this.product.isVspButtonEnabled) {
      this.openVspDrawer();
      return;
    }
    if (this.showNotificationButton()) {
      this.triggerHiddenNotificationButton();
    } else {
      this.triggerHiddenAddToCartButton();
    }
  };

  private openVspDrawer() {
    const payload = {
      syan: this.product.syan,
      ...(this.product.colorId ? {farbId: this.product.colorId} : {}),
      listName: 'wishlist_cta',
    };
    document.dispatchEvent(new CustomEvent('suchen:vsp:alternativeprodukte', {detail: payload}));
    const button = this.shadowRoot?.querySelector<HTMLButtonElement>('[data-test-ents-inline-primary-button]');
    button?.setAttribute('data-test-ents-vsp-button-payload-emitted', JSON.stringify(payload));
  }

  private renderHiddenVspButton() {
    return html`
      <ents-vsp-button
        style="display: none;"
        .name="${this.getSmallProductInfo()}"
        syan="${this.product.syan}"
        color-id="${this.product.colorId}"
      ></ents-vsp-button>
    `;
  }

  private triggerHiddenAddToCartButton() {
    if (!this.selectedArticle?.salesInformationId) {
      this.dispatchEvent(new CustomEvent<OpenSizeLayerContainer>('ents:size-layer:open', {
        detail: {
          addToCartAfterSizeSelection: true,
          tracking: {
            context: 'wishlist_cart_size_menu',
          },
        },
        bubbles: true,
        composed: true,
      }));
      return;
    }

    const button = this.shadowRoot?.querySelector<TriggerableElement>('ents-add-to-cart-button[data-inline-hidden-add]');
    if (button) {
      button.trigger = true;
    }
  }

  private triggerHiddenNotificationButton() {
    if (!this.selectedArticle?.salesInformationId) {
      this.dispatchEvent(new CustomEvent<OpenSizeLayerContainer>('ents:size-layer:open', {
        detail: {
          addToCartAfterSizeSelection: false,
          tracking: {
            context: 'wishlist_cart_size_menu',
          },
        },
        bubbles: true,
        composed: true,
      }));
      return;
    }

    const button = this.shadowRoot?.querySelector<TriggerableElement>('ents-notify-me-button[data-inline-hidden-notify]');
    if (button) {
      button.trigger = true;
    }
  }

  // === abtest-ents-t6727-favoriten-artikelkachel-redesign Product Tile Redesign END ===

  private updateProductBanner() {
    const eventPayload = {
      syan: this.product.syan,
      farbId: this.product.colorId,
      artikelId: this.selectedArticle?.id,
    };
    document.dispatchEvent(new CustomEvent('bewe:ents:variantenwechsel', {detail: eventPayload})); // shared event used by other teams (e.g. BRTN, CAMP, KAUF)
    const actionMenuButton = this.shadowRoot?.querySelector<HTMLButtonElement>('[data-test-ents-action-button]');
    actionMenuButton?.setAttribute('data-test-ents-product-banner-payload-emitted', JSON.stringify(eventPayload));
  }

  private renderPrimaryButton() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return html`${this.renderRedesignInlineControls()}`;
    }
    if (this.showNotificationButton()) {
      return html`${this.renderNotificationButton()}`;
    }
    return html`${this.renderAddToCartButton()}`;
  }

  protected render(): TemplateResult {
    const articleClasses = this.wishlistAppConfig.productTileRedesign
      ? 'ents-wishlist-product--redesign'
      : '';
    return html`
      <article class="${articleClasses}">
        ${this.product.price ? this.renderAvailableProduct() : this.renderUnavailableProduct()}
      </article>
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if (changedProperties.size === 0) {
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }

  public forceFullRendering(): void {
    this.forcedFullRendering = true;
    this.isInViewport = true;
  }

  public setInViewportDebounced(isInViewport: boolean): void {
    clearTimeout(this.viewportDebounceTimer);
    this.viewportDebounceTimer = window.setTimeout(() => {
      if (this.forcedFullRendering && !isInViewport) return;
      this.isInViewport = isInViewport;
    }, 300);
  }

  public trackViewDebounced(isInViewport: boolean): void {
    if (this.hasTrackedView) return;

    clearTimeout(this.trackingDebounceTimer);
    this.trackingDebounceTimer = window.setTimeout(() => {
      if (this.hasTrackedView || !isInViewport) return;
      this.hasTrackedView = true;
      this.trackView();
      this.trackingObserver?.unobserve(this);
    }, 300);
  }

  private trackView() {
    const hasActiveFilter = this.filterMenu.getAppliedActiveAndNotDefaultOptionsCount() > 0;
    const filteredListItemsCount = hasActiveFilter ? this.filterMenu.resultCount : undefined;
    productListView(this.product, this.selectedArticle, this.position, this.loggedIn, filteredListItemsCount);
  }

  private productListClickEvent() {
    trackWishlistClick(this.product, this.selectedArticle, this.position, this.countListItems, this.loggedIn, this.getTrackingUrl());
  }

  private get useMinimalRender(): boolean {
    return this.countListItems >= 100 && !this.isInViewport;
  }
}
